import React, { useState, useEffect, useContext } from 'react';
import { Card, Typography, Textarea, Button, Alert, Input } from '@material-tailwind/react';
import axios from 'axios';
import { AuthContext } from '../../AuthContext';
import { rootUrl } from '../../../../config/config';

export default function JobCardNotes({ jobCard, onUpdate, locked = false }) {
    const { user } = useContext(AuthContext);
    const [diagnosis, setDiagnosis] = useState('');
    const [workDone, setWorkDone] = useState('');
    const [driveLink, setDriveLink] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        setDiagnosis(jobCard?.diagnosis || '');
        setWorkDone(jobCard?.work_performed || jobCard?.work_done || '');
        setDriveLink(jobCard?.drive_link || '');
        setIsEditing(false);
        setError(null);
        setSuccess(false);
    }, [jobCard]);

    const handleUpdate = async () => {
        try {
            setLoading(true);
            setError(null);
            setSuccess(false);

            const response = await axios.put(
                `${rootUrl}/api/job-cards/${jobCard.id}`,
                {
                    diagnosis,
                    work_performed: workDone,
                    drive_link: driveLink,
                    updated_by: user?.id || user?.email
                },
                { headers: { Authorization: `Bearer ${user?.token}` } }
            );

            if (response.data.success) {
                setSuccess(true);
                setIsEditing(false);
                if (onUpdate) {
                    onUpdate();
                }
                setTimeout(() => setSuccess(false), 3000);
            } else {
                setError(response.data.message || 'Failed to update');
            }
        } catch (err) {
            console.error('Error updating:', err);
            setError(err.response?.data?.message || 'Failed to update');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setDiagnosis(jobCard?.diagnosis || '');
        setWorkDone(jobCard?.work_done || '');
        setDriveLink(jobCard?.drive_link || '');
        setIsEditing(false);
        setError(null);
    };

    return (
        <div className="py-6">
            {error && <Alert color="red" className="mb-4">{error}</Alert>}
            {success && <Alert color="green" className="mb-4">Updated successfully</Alert>}

            {locked && (
                <Alert color="yellow" className="mb-4">
                    This job card is completed/closed. Information cannot be modified.
                </Alert>
            )}

            <div className="grid grid-cols-1 gap-6">
                {/* Diagnosis */}
                <Card className="p-6">
                    <div className="flex justify-between items-center mb-4">
                        <Typography variant="h6">Diagnosis</Typography>
                    </div>
                    <Textarea
                        value={diagnosis}
                        onChange={(e) => setDiagnosis(e.target.value)}
                        readOnly={!isEditing}
                        rows={4}
                        placeholder="Enter diagnosis"
                        disabled={locked}
                    />
                </Card>

                {/* Work Done */}
                <Card className="p-6">
                    <div className="flex justify-between items-center mb-4">
                        <Typography variant="h6">Work Done</Typography>
                    </div>
                    <Textarea
                        value={workDone}
                        onChange={(e) => setWorkDone(e.target.value)}
                        readOnly={!isEditing}
                        rows={4}
                        placeholder="Enter work done"
                        disabled={locked}
                    />
                </Card>

                {/* Drive Link */}
                <Card className="p-6">
                    <div className="flex justify-between items-center mb-4">
                        <Typography variant="h6">Drive Link</Typography>
                    </div>
                    <Input
                        type="url"
                        value={driveLink}
                        onChange={(e) => setDriveLink(e.target.value)}
                        readOnly={!isEditing}
                        placeholder="Enter drive link (e.g., https://drive.google.com/...)"
                        disabled={locked}
                        label="Drive Link URL"
                    />
                    {driveLink && isEditing && (
                        <Typography variant="small" className="text-gray-600 mt-2">
                            Click the link to open: <a href={driveLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">View Drive Link</a>
                        </Typography>
                    )}
                </Card>

                {/* Action Buttons */}
                <div className="flex gap-3">
                    {!locked && (
                        <>
                            {!isEditing ? (
                                <Button
                                    color="blue"
                                    onClick={() => setIsEditing(true)}
                                >
                                    Edit
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        color="blue"
                                        onClick={handleUpdate}
                                        loading={loading}
                                        disabled={loading}
                                    >
                                        Update
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="gray"
                                        onClick={handleCancel}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </Button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
